
import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient as backendClient } from '@/lib/api-client';
import { User } from '@/types';
import { getCustomerSession, setCustomerSession } from '@/lib/app-session';
// Customers authenticate with mobile OTP only; there is no customer password.

interface MobileLoginPayload { mobile: string; sessionToken: string }
interface MobileSignupPayload { mobile: string; sessionToken: string; firstName: string; lastName: string }

interface AuthContextType {
  user: User | null;
  loginWithMobile: (p: MobileLoginPayload) => Promise<{ success: boolean; error?: string }>;
  signupWithMobile: (p: MobileSignupPayload) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (updatedUser: Partial<User>) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

function toUser(row: any): User {
  return {
    id: row.id.toString(),
    email: row.email || '',
    firstName: row.first_name,
    lastName: row.last_name,
    mobile: row.mobile,
    role: row.email === 'admin@store.com' ? 'admin' : 'customer',
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && !getCustomerSession()) {
      // A login from before server-issued session tokens existed: the server can't
      // verify who this is, so ask the user to log in again (one time only).
      localStorage.removeItem('user');
    } else if (storedUser) {
      setUser(JSON.parse(storedUser));
      // Drop the session if the server no longer accepts the token (expired / revoked).
      backendClient.rpc('app_whoami').then(({ data, error }) => {
        if (!error && (data === null || data === undefined)) {
          setCustomerSession(null);
          localStorage.removeItem('user');
          setUser(null);
        }
      });
    }
    setIsLoading(false);

    const syncFromStorage = () => {
      const u = localStorage.getItem('user');
      if (u) { try { setUser(JSON.parse(u)); } catch {} }
    };
    const onStorage = (e: StorageEvent) => { if (e.key === 'user' || e.key === 'auth-event') syncFromStorage(); };
    window.addEventListener('storage', onStorage);

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('auth');
      channel.onmessage = (ev) => {
        if (ev.data?.type === 'verified' && ev.data?.user) {
          setUser(ev.data.user);
          localStorage.setItem('user', JSON.stringify(ev.data.user));
        }
      };
    } catch {}

    return () => { window.removeEventListener('storage', onStorage); channel?.close(); };
  }, []);

  const persistUser = (row: any) => {
    const u = toUser(row);
    setUser(u);
    localStorage.setItem('user', JSON.stringify(u));
    return u;
  };

  // ---- Mobile-OTP auth ----
  const loginWithMobile = async ({ mobile, sessionToken }: MobileLoginPayload) => {
    setIsLoading(true);
    try {
      const { data, error } = await backendClient.rpc('login_with_mobile_otp', {
        p_mobile: mobile, p_session_token: sessionToken,
      });
      if (error) { setIsLoading(false); return { success: false, error: error.message }; }
      // login_with_mobile_otp RETURNS TABLE(...) -- supabase-js returns an
      // array for that, not a single object (unlike the old RETURNS
      // public.users_profiles signature this replaced). Unwrap it.
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) { setIsLoading(false); return { success: false, error: 'User not found.' }; }
      setCustomerSession(row.session_token);
      persistUser(row);
      setIsLoading(false);
      return { success: true };
    } catch (e: any) { setIsLoading(false); return { success: false, error: e.message }; }
  };

  const signupWithMobile = async ({ mobile, sessionToken, firstName, lastName }: MobileSignupPayload) => {
    setIsLoading(true);
    try {
      const { data, error } = await backendClient.rpc('signup_with_mobile_otp', {
        p_mobile: mobile, p_session_token: sessionToken,
        p_first_name: firstName, p_last_name: lastName,
      });
      if (error) { setIsLoading(false); return { success: false, error: error.message }; }
      // Same RETURNS TABLE(...) array-unwrap as loginWithMobile above.
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) { setIsLoading(false); return { success: false, error: 'Signup failed.' }; }
      setCustomerSession(row.session_token);
      persistUser(row);
      setIsLoading(false);
      return { success: true };
    } catch (e: any) { setIsLoading(false); return { success: false, error: e.message }; }
  };

  const updateUser = (updatedUser: Partial<User>) => {
    if (user) {
      const newUser = { ...user, ...updatedUser };
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      const newUser = updatedUser as User;
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
    }
  };

  const logout = () => {
    // The user is logged out on screen immediately, but the session token must stay
    // available until the revoke request has actually been sent: the database client
    // attaches it asynchronously, so removing it right away would send the revoke
    // without a token and leave the session alive on the server.
    const forgetToken = () => setCustomerSession(null);
    Promise.race([
      backendClient.rpc('app_logout').then(() => undefined, () => undefined),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]).then(forgetToken);
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{
      user, loginWithMobile, signupWithMobile,
      logout, updateUser, isLoading,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
